import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { 
  Smartphone, 
  Save, 
  RefreshCw,
  Palette,
  Layout,
  Type,
  Image,
  Globe,
  Bell,
  Shield,
  ExternalLink,
  Eye
} from 'lucide-react';
import './MiniAppSettings.css';

function MiniAppSettings() {
  const [settings, setSettings] = useState({
    app_name: '',
    app_description: '',
    app_url: '',
    primary_color: '#6366f1',
    secondary_color: '#a855f7',
    background_color: '#0a0a12',
    text_color: '#ffffff',
    logo_url: '',
    favicon_url: '',
    welcome_message: '',
    notifications_enabled: true,
    analytics_enabled: true,
    maintenance_mode: false,
    news_per_page: 10,
    show_author: true,
    show_date: true,
    show_views: true,
    show_likes: true,
    enable_comments: true,
    enable_bookmarks: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/miniapp/settings');
      if (response.data.success && response.data.settings) {
        setSettings(prev => ({ ...prev, ...response.data.settings }));
      }
    } catch (error) {
      console.error('Ошибка загрузки настроек:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/miniapp/settings', settings);
      alert('Настройки сохранены!');
    } catch (error) {
      console.error('Ошибка сохранения:', error);
      alert('Ошибка сохранения настроек');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'general', label: 'Основные', icon: Layout },
    { id: 'appearance', label: 'Внешний вид', icon: Palette },
    { id: 'content', label: 'Контент', icon: Type },
    { id: 'features', label: 'Функции', icon: Shield }
  ];

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <span>Загрузка...</span>
      </div>
    );
  }

  return (
    <div className="miniapp-page fade-in">
      <div className="page-header">
        <h1 className="page-title">Настройки MiniApp</h1>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={loadSettings}>
            <RefreshCw size={18} />
            Обновить
          </button>
          {settings.app_url && (
            <a 
              href={settings.app_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn btn-secondary"
            >
              <ExternalLink size={18} />
              Открыть
            </a>
          )}
          <button 
            className="btn btn-primary" 
            onClick={saveSettings}
            disabled={saving}
          >
            <Save size={18} />
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </div>

      <div className="settings-layout">
        {/* Tabs */}
        <div className="settings-tabs">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="settings-content">
          {/* General Tab */}
          {activeTab === 'general' && (
            <div className="settings-section">
              <h2 className="section-title">Основные настройки</h2>
              
              <div className="input-group">
                <label className="input-label">Название приложения</label>
                <input
                  type="text"
                  className="input"
                  value={settings.app_name}
                  onChange={(e) => setSettings({...settings, app_name: e.target.value})}
                  placeholder="Ballet Post"
                />
              </div>

              <div className="input-group">
                <label className="input-label">Описание</label>
                <textarea
                  className="input textarea"
                  value={settings.app_description}
                  onChange={(e) => setSettings({...settings, app_description: e.target.value})}
                  placeholder="Описание вашего MiniApp..."
                  rows={3}
                />
              </div>

              <div className="input-group">
                <label className="input-label">URL приложения</label>
                <input
                  type="url"
                  className="input"
                  value={settings.app_url}
                  onChange={(e) => setSettings({...settings, app_url: e.target.value})}
                  placeholder="https://..."
                />
              </div>

              <div className="input-group">
                <label className="input-label">Приветственное сообщение</label>
                <textarea
                  className="input textarea"
                  value={settings.welcome_message}
                  onChange={(e) => setSettings({...settings, welcome_message: e.target.value})}
                  placeholder="Сообщение для новых пользователей..."
                  rows={3}
                />
              </div>

              <div className="toggle-group">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={settings.maintenance_mode}
                    onChange={(e) => setSettings({...settings, maintenance_mode: e.target.checked})}
                  />
                  <span className="toggle-switch"></span>
                  <div className="toggle-text">
                    <span className="toggle-title">Режим обслуживания</span>
                    <span className="toggle-desc">Отображает страницу обслуживания для пользователей</span>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <div className="settings-section">
              <h2 className="section-title">Внешний вид</h2>

              {/* Custom Logo Upload */}
              <div className="input-group">
                <label className="input-label">Кастомный логотип (главный экран)</label>
                <input
                  type="file"
                  accept="image/*"
                  className="input"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    
                    const formData = new FormData();
                    formData.append('logo', file);
                    
                    try {
                      const response = await api.post('/api/admin/miniapp-settings/upload-logo', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      });
                      if (response.data.success) {
                        alert('Логотип загружен!');
                        loadSettings();
                      }
                    } catch (error) {
                      console.error('Ошибка загрузки:', error);
                      alert('Ошибка загрузки логотипа');
                    }
                  }}
                />
                <small style={{ color: '#888', marginTop: '8px', display: 'block' }}>
                  Рекомендуемый размер: 160x160px. Отображается на главном экране над текстом "Ballet Post"
                </small>
                {settings.custom_logo_url && (
                  <div className="preview-image" style={{ marginTop: '12px' }}>
                    <img src={settings.custom_logo_url} alt="Custom logo preview" style={{ maxWidth: '160px', maxHeight: '160px' }} />
                  </div>
                )}
              </div>

              <div className="color-grid">
                <div className="input-group">
                  <label className="input-label">Основной цвет</label>
                  <div className="color-input">
                    <input
                      type="color"
                      value={settings.primary_color}
                      onChange={(e) => setSettings({...settings, primary_color: e.target.value})}
                    />
                    <input
                      type="text"
                      className="input"
                      value={settings.primary_color}
                      onChange={(e) => setSettings({...settings, primary_color: e.target.value})}
                    />
                  </div>
                </div>

                <div className="input-group">
                  <label className="input-label">Вторичный цвет</label>
                  <div className="color-input">
                    <input
                      type="color"
                      value={settings.secondary_color}
                      onChange={(e) => setSettings({...settings, secondary_color: e.target.value})}
                    />
                    <input
                      type="text"
                      className="input"
                      value={settings.secondary_color}
                      onChange={(e) => setSettings({...settings, secondary_color: e.target.value})}
                    />
                  </div>
                </div>

                <div className="input-group">
                  <label className="input-label">Цвет фона</label>
                  <div className="color-input">
                    <input
                      type="color"
                      value={settings.background_color}
                      onChange={(e) => setSettings({...settings, background_color: e.target.value})}
                    />
                    <input
                      type="text"
                      className="input"
                      value={settings.background_color}
                      onChange={(e) => setSettings({...settings, background_color: e.target.value})}
                    />
                  </div>
                </div>

                <div className="input-group">
                  <label className="input-label">Цвет текста</label>
                  <div className="color-input">
                    <input
                      type="color"
                      value={settings.text_color}
                      onChange={(e) => setSettings({...settings, text_color: e.target.value})}
                    />
                    <input
                      type="text"
                      className="input"
                      value={settings.text_color}
                      onChange={(e) => setSettings({...settings, text_color: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div className="input-group">
                <label className="input-label">URL логотипа</label>
                <input
                  type="url"
                  className="input"
                  value={settings.logo_url}
                  onChange={(e) => setSettings({...settings, logo_url: e.target.value})}
                  placeholder="https://..."
                />
                {settings.logo_url && (
                  <div className="preview-image">
                    <img src={settings.logo_url} alt="Logo preview" />
                  </div>
                )}
              </div>

              <div className="input-group">
                <label className="input-label">URL favicon</label>
                <input
                  type="url"
                  className="input"
                  value={settings.favicon_url}
                  onChange={(e) => setSettings({...settings, favicon_url: e.target.value})}
                  placeholder="https://..."
                />
              </div>
            </div>
          )}

          {/* Content Tab */}
          {activeTab === 'content' && (
            <div className="settings-section">
              <h2 className="section-title">Настройки контента</h2>

              <div className="input-group">
                <label className="input-label">Новостей на странице</label>
                <input
                  type="number"
                  className="input"
                  value={settings.news_per_page}
                  onChange={(e) => setSettings({...settings, news_per_page: parseInt(e.target.value) || 10})}
                  min={5}
                  max={50}
                />
              </div>

              <div className="toggles-grid">
                <div className="toggle-group">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={settings.show_author}
                      onChange={(e) => setSettings({...settings, show_author: e.target.checked})}
                    />
                    <span className="toggle-switch"></span>
                    <span>Показывать автора</span>
                  </label>
                </div>

                <div className="toggle-group">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={settings.show_date}
                      onChange={(e) => setSettings({...settings, show_date: e.target.checked})}
                    />
                    <span className="toggle-switch"></span>
                    <span>Показывать дату</span>
                  </label>
                </div>

                <div className="toggle-group">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={settings.show_views}
                      onChange={(e) => setSettings({...settings, show_views: e.target.checked})}
                    />
                    <span className="toggle-switch"></span>
                    <span>Показывать просмотры</span>
                  </label>
                </div>

                <div className="toggle-group">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={settings.show_likes}
                      onChange={(e) => setSettings({...settings, show_likes: e.target.checked})}
                    />
                    <span className="toggle-switch"></span>
                    <span>Показывать лайки</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Features Tab */}
          {activeTab === 'features' && (
            <div className="settings-section">
              <h2 className="section-title">Функции</h2>

              <div className="feature-cards">
                <div className="feature-card">
                  <div className="feature-header">
                    <Bell size={20} />
                    <h3>Уведомления</h3>
                  </div>
                  <p>Push-уведомления для пользователей</p>
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={settings.notifications_enabled}
                      onChange={(e) => setSettings({...settings, notifications_enabled: e.target.checked})}
                    />
                    <span className="toggle-switch"></span>
                    <span>Включено</span>
                  </label>
                </div>

                <div className="feature-card">
                  <div className="feature-header">
                    <Eye size={20} />
                    <h3>Аналитика</h3>
                  </div>
                  <p>Сбор статистики использования</p>
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={settings.analytics_enabled}
                      onChange={(e) => setSettings({...settings, analytics_enabled: e.target.checked})}
                    />
                    <span className="toggle-switch"></span>
                    <span>Включено</span>
                  </label>
                </div>

                <div className="feature-card">
                  <div className="feature-header">
                    <Type size={20} />
                    <h3>Комментарии</h3>
                  </div>
                  <p>Возможность комментировать новости</p>
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={settings.enable_comments}
                      onChange={(e) => setSettings({...settings, enable_comments: e.target.checked})}
                    />
                    <span className="toggle-switch"></span>
                    <span>Включено</span>
                  </label>
                </div>

                <div className="feature-card">
                  <div className="feature-header">
                    <Image size={20} />
                    <h3>Закладки</h3>
                  </div>
                  <p>Сохранение новостей в избранное</p>
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={settings.enable_bookmarks}
                      onChange={(e) => setSettings({...settings, enable_bookmarks: e.target.checked})}
                    />
                    <span className="toggle-switch"></span>
                    <span>Включено</span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MiniAppSettings;
